import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as api from "../api/InterviewQaApi";
import type { InterviewCategory, InterviewQuestion, QuestionCreate, QuestionUpdate, CategoryCreate, CategoryUpdate, QuestionFilters } from "../api/InterviewQaApi";

interface InterviewQaContextValue {
  categories: InterviewCategory[];
  questions: InterviewQuestion[];
  categoriesLoading: boolean;
  questionsLoading: boolean;
  error: string;
  filters: QuestionFilters;
  activeCategoryId: number | null;

  setActiveCategoryId: (id: number | null) => void;
  setFilters: (f: QuestionFilters) => void;
  loadCategories: () => Promise<void>;
  loadQuestions: (f?: QuestionFilters) => Promise<void>;

  createCategory: (data: CategoryCreate) => Promise<void>;
  updateCategory: (id: number, data: CategoryUpdate) => Promise<void>;
  deleteCategory: (id: number) => Promise<void>;

  createQuestion: (data: QuestionCreate) => Promise<void>;
  updateQuestion: (id: number, data: QuestionUpdate) => Promise<void>;
  deleteQuestion: (id: number) => Promise<void>;
}

const InterviewQaContext = createContext<InterviewQaContextValue | null>(null);

export function InterviewQaProvider({ children }: { children: React.ReactNode }) {
  const [categories, setCategories] = useState<InterviewCategory[]>([]);
  const [questions, setQuestions] = useState<InterviewQuestion[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [filters, setFiltersState] = useState<QuestionFilters>({});

  const setFilters = useCallback((f: QuestionFilters) => {
    setFiltersState(f);
  }, []);

  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const data = await api.listCategories();
      setCategories(data);
    } catch {
      setError("Failed to load categories");
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const loadQuestions = useCallback(async (f?: QuestionFilters) => {
    setQuestionsLoading(true);
    try {
      const data = await api.listQuestions(f ?? filters);
      setQuestions(data);
    } catch {
      setError("Failed to load questions");
    } finally {
      setQuestionsLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => {
    const f: QuestionFilters = { ...filters };
    if (activeCategoryId !== null) f.category_id = activeCategoryId;
    else delete f.category_id;
    loadQuestions(f);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategoryId, filters.difficulty, filters.status, filters.search]);

  const createCategory = useCallback(async (data: CategoryCreate) => {
    await api.createCategory(data);
    await loadCategories();
  }, [loadCategories]);

  const updateCategory = useCallback(async (id: number, data: CategoryUpdate) => {
    await api.updateCategory(id, data);
    await loadCategories();
  }, [loadCategories]);

  const deleteCategory = useCallback(async (id: number) => {
    await api.deleteCategory(id);
    if (activeCategoryId === id) setActiveCategoryId(null);
    await loadCategories();
  }, [loadCategories, activeCategoryId]);

  const createQuestion = useCallback(async (data: QuestionCreate) => {
    await api.createQuestion(data);
    const f: QuestionFilters = { ...filters };
    if (activeCategoryId !== null) f.category_id = activeCategoryId;
    await loadQuestions(f);
    await loadCategories();
  }, [filters, activeCategoryId, loadQuestions, loadCategories]);

  const updateQuestion = useCallback(async (id: number, data: QuestionUpdate) => {
    await api.updateQuestion(id, data);
    const f: QuestionFilters = { ...filters };
    if (activeCategoryId !== null) f.category_id = activeCategoryId;
    await loadQuestions(f);
    await loadCategories();
  }, [filters, activeCategoryId, loadQuestions, loadCategories]);

  const deleteQuestion = useCallback(async (id: number) => {
    await api.deleteQuestion(id);
    const f: QuestionFilters = { ...filters };
    if (activeCategoryId !== null) f.category_id = activeCategoryId;
    await loadQuestions(f);
    await loadCategories();
  }, [filters, activeCategoryId, loadQuestions, loadCategories]);

  const value = useMemo<InterviewQaContextValue>(() => ({
    categories, questions, categoriesLoading, questionsLoading, error,
    filters, activeCategoryId,
    setActiveCategoryId, setFilters, loadCategories, loadQuestions,
    createCategory, updateCategory, deleteCategory,
    createQuestion, updateQuestion, deleteQuestion,
  }), [
    categories, questions, categoriesLoading, questionsLoading, error,
    filters, activeCategoryId,
    setActiveCategoryId, setFilters, loadCategories, loadQuestions,
    createCategory, updateCategory, deleteCategory,
    createQuestion, updateQuestion, deleteQuestion,
  ]);

  return <InterviewQaContext.Provider value={value}>{children}</InterviewQaContext.Provider>;
}

export function useInterviewQa(): InterviewQaContextValue {
  const ctx = useContext(InterviewQaContext);
  if (!ctx) throw new Error("useInterviewQa must be used within InterviewQaProvider");
  return ctx;
}
